class HomepageCategorySection extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    this.desktopCols = parseInt(this.dataset.desktopCols, 10) || 4;
    this.tabletCols = 3;
    this.mobileLayout = this.dataset.mobileLayout || 'carousel';
    this.mobileSlides = parseFloat(this.dataset.mobileSlides) || 2.5;
    this.desktopMq = window.matchMedia('(min-width: 1025px)');
    this.tabletMq = window.matchMedia('(min-width: 768px)');
    this.swipers = new Map();

    this.bindTabs();
    this.whenSwiperReady(() => {
      if (!this.isConnected) return;
      this.syncAllPanels();
      this.onBreakpointChange = () => this.syncAllPanels();
      if (this.desktopMq.addEventListener) {
        this.desktopMq.addEventListener('change', this.onBreakpointChange);
        this.tabletMq.addEventListener('change', this.onBreakpointChange);
      } else {
        this.desktopMq.addListener(this.onBreakpointChange);
        this.tabletMq.addListener(this.onBreakpointChange);
      }
    });
  }

  disconnectedCallback() {
    this.clearSwiperReadyTimer();
    if (this.onBreakpointChange) {
      if (this.desktopMq && this.desktopMq.removeEventListener) {
        this.desktopMq.removeEventListener('change', this.onBreakpointChange);
        this.tabletMq.removeEventListener('change', this.onBreakpointChange);
      } else if (this.desktopMq) {
        this.desktopMq.removeListener(this.onBreakpointChange);
        this.tabletMq.removeListener(this.onBreakpointChange);
      }
    }
    this.swipers.forEach((swiper) => {
      swiper.destroy(true, true);
    });
    this.swipers.clear();
    this._bound = false;
  }

  clearSwiperReadyTimer() {
    if (!this._swiperReadyTimer) return;
    clearInterval(this._swiperReadyTimer);
    this._swiperReadyTimer = null;
  }

  whenSwiperReady(callback) {
    this.clearSwiperReadyTimer();
    if (typeof Swiper !== 'undefined') {
      callback();
      return;
    }
    var self = this;
    var attempts = 0;
    this._swiperReadyTimer = setInterval(function () {
      attempts += 1;
      if (typeof Swiper !== 'undefined') {
        self.clearSwiperReadyTimer();
        callback();
      } else if (attempts > 80) {
        self.clearSwiperReadyTimer();
      }
    }, 50);
  }

  isDesktop() {
    return this.desktopMq.matches;
  }

  isTablet() {
    return this.tabletMq.matches && !this.desktopMq.matches;
  }

  getSpaceBetween() {
    if (this.isDesktop()) return 22;
    return 16;
  }

  // Desktop 6-up card size (~200px). Tablet keeps similar width so 1:1 height stays close.
  getTargetCardWidth() {
    return 200;
  }

  getDesktopSlidesPerView() {
    return Math.min(6, this.desktopCols || 6);
  }

  getTabletSlidesPerView(panel) {
    var gap = this.getSpaceBetween();
    var targetCard = this.getTargetCardWidth();
    var root = panel && panel.querySelector('.category-section__swiper');
    var available = root && root.clientWidth > 0 ? root.clientWidth : Math.max(window.innerWidth - 40, 600);
    var fit = Math.floor((available + gap) / (targetCard + gap));
    return Math.max(3, Math.min(5, fit || 3));
  }

  getSlidesPerView(panel) {
    if (this.isDesktop()) return this.getDesktopSlidesPerView();
    if (this.isTablet()) return this.getTabletSlidesPerView(panel);
    return this.mobileSlides;
  }

  bindTabs() {
    var tabs = this.querySelectorAll('[role="tab"]');
    var self = this;
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        self.activateTab(tab);
      });
      tab.addEventListener('keydown', function (event) {
        self.onTabKeydown(event);
      });
    });
  }

  onTabKeydown(event) {
    var tabs = Array.prototype.slice.call(this.querySelectorAll('[role="tab"]'));
    var index = tabs.indexOf(event.target);
    if (index < 0) return;

    var next = index;
    if (event.key === 'ArrowRight') {
      next = (index + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft') {
      next = (index - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'Home') {
      next = 0;
    } else if (event.key === 'End') {
      next = tabs.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    tabs[next].focus();
    this.activateTab(tabs[next]);
  }

  activateTab(tab) {
    var targetId = tab.getAttribute('aria-controls');
    var tabs = this.querySelectorAll('[role="tab"]');
    var panels = this.querySelectorAll('[role="tabpanel"]');
    var self = this;

    tabs.forEach(function (item) {
      var selected = item === tab;
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
      item.tabIndex = selected ? 0 : -1;
    });

    panels.forEach(function (panel) {
      panel.hidden = panel.id !== targetId;
    });

    var activePanel = document.getElementById(targetId);
    requestAnimationFrame(function () {
      self.syncPanel(activePanel);
    });
  }

  shouldEnableCarousel(panel) {
    var count = parseInt(panel.dataset.cardCount, 10) || 0;
    if (this.isDesktop()) {
      return count > this.getDesktopSlidesPerView();
    }
    if (this.isTablet()) {
      return this.mobileLayout === 'carousel' && count > this.getTabletSlidesPerView(panel);
    }
    if (this.mobileLayout !== 'carousel') return false;
    return count > this.mobileSlides;
  }

  syncAllPanels() {
    var self = this;
    this.querySelectorAll('.category-section__panel').forEach(function (panel) {
      if (panel.hidden) return;
      self.syncPanel(panel);
    });
  }

  syncPanel(panel) {
    if (!panel) return;

    var enable = this.shouldEnableCarousel(panel);
    var existing = this.swipers.get(panel);

    var controls = panel.querySelector('.category-section__controls');

    if (enable) {
      panel.classList.add('is-carousel');
      if (controls) controls.hidden = false;
      if (existing) {
        var nextPerView = this.getSlidesPerView(panel);
        var nextGap = this.getSpaceBetween();
        if (
          existing.params.slidesPerView !== nextPerView ||
          existing.params.spaceBetween !== nextGap
        ) {
          existing.params.slidesPerView = nextPerView;
          existing.params.spaceBetween = nextGap;
          existing.update();
          existing.slideTo(0, 0);
        }
        this.updateProgress(panel, existing);
      } else {
        this.initSwiper(panel);
      }
    } else {
      panel.classList.remove('is-carousel');
      if (controls) controls.hidden = true;
      if (existing) {
        existing.destroy(true, true);
        this.swipers.delete(panel);
      }
    }
  }

  initSwiper(panel) {
    var root = panel.querySelector('.category-section__swiper');
    if (!root || typeof Swiper === 'undefined') return;

    var self = this;
    var swiper = new Swiper(root, {
      slidesPerView: this.getSlidesPerView(panel),
      spaceBetween: this.getSpaceBetween(),
      watchOverflow: true,
      watchSlidesProgress: true,
      speed: 400,
      mousewheel: {
        forceToAxis: true
      },
      navigation: {
        nextEl: panel.querySelector('.category-section__next'),
        prevEl: panel.querySelector('.category-section__prev')
      },
      on: {
        progress: function (instance) {
          self.updateProgress(panel, instance);
        },
        resize: function (instance) {
          var nextPerView = self.getSlidesPerView(panel);
          var nextGap = self.getSpaceBetween();
          if (
            instance.params.slidesPerView !== nextPerView ||
            instance.params.spaceBetween !== nextGap
          ) {
            instance.params.slidesPerView = nextPerView;
            instance.params.spaceBetween = nextGap;
            instance.update();
          }
          self.updateProgress(panel, instance);
        }
      }
    });

    this.swipers.set(panel, swiper);
    this.updateProgress(panel, swiper);
  }

  updateProgress(panel, swiper) {
    var fill = panel.querySelector('.category-section__progress-fill');
    if (!fill || !swiper) return;

    if (swiper.isLocked) {
      fill.style.width = '100%';
      return;
    }

    var min = swiper.size / Math.max(swiper.virtualSize, 1);
    var width = Math.min(1, Math.max(min, min + swiper.progress * (1 - min)));
    fill.style.width = width * 100 + '%';
  }
}

if (!customElements.get('homepage-category-section')) {
  customElements.define('homepage-category-section', HomepageCategorySection);
}
